import { Divider, ListItemText, MenuItem, SvgIcon, ToggleButton, Typography } from '@mui/material';
import Menu from '@mui/material/Menu';
import React, { useState } from 'react';
import StyledToggleButtonGroup from '../lexical/StyledToggleButtonGroup';
import I18n from '../i18n/I18n';
import { Icons } from '../icons/Icons';
import { KeyValueMap } from '../KeyValueMap';
import { ActionType } from './ActionType';

export type SelectedType = string[] | string | null;

export type ResponsiveToolbarProps = {
    options: KeyValueMap<string, ActionType>;
    multi?: boolean;
    icon?: typeof SvgIcon;
    disabled?: boolean;
    selected?: SelectedType;
    onSelect?: (selected: SelectedType) => void;
    isSmall?: boolean;
    label?: React.ReactNode;
};

const Dropdown = (props: ResponsiveToolbarProps) => {
    const { disabled, multi, options, selected, icon, label, onSelect } = props;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const handleMenuClose = () => {
        setIsMenuOpen(false);
        setAnchorEl(null);
    };
    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setIsMenuOpen(true);
    };

    const selectedKey = typeof selected === 'string' ? selected : null;
    const DropdownIcon =
        multi || !selectedKey || !options[selectedKey] || !options[selectedKey].icon ? icon : options[selectedKey].icon;

    return (
        <>
            <StyledToggleButtonGroup exclusive size={'small'}>
                <ToggleButton
                    disabled={disabled}
                    value={selected}
                    onClick={handleMenuOpen}
                    sx={{ textTransform: 'unset' }}
                >
                    {Boolean(DropdownIcon) && <DropdownIcon fontSize={'small'} />}
                    {Boolean(label) && (
                        <Typography>{typeof label === 'string' ? <I18n token={label} /> : label}</Typography>
                    )}
                    <Icons.KeyboardArrowDown />
                </ToggleButton>
            </StyledToggleButtonGroup>
            <Menu
                id={'paragraph-style-menu'}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
                open={isMenuOpen}
                onClose={handleMenuClose}
            >
                {Object.keys(options).map((key, index) => {
                    const action: ActionType = options[key];
                    const items = [];

                    items.push(
                        <MenuItem
                            key={`item-${index}`}
                            disabled={action.disabled}
                            onClick={(event) => {
                                if (typeof onSelect === 'function') {
                                    onSelect(key);
                                } else if (typeof action.onClick === 'function') {
                                    action.onClick(event);
                                }
                                handleMenuClose();
                            }}
                            selected={selected && Array.isArray(selected) ? selected.includes(key) : key === selected}
                        >
                            {action.icon && <action.icon fontSize="small" sx={{ mr: 1 }} />}
                            <ListItemText>
                                {typeof action.label === 'string' ? <I18n token={action.label} /> : action.label}
                            </ListItemText>
                        </MenuItem>
                    );

                    if (action.divider) {
                        items.push(<Divider key={`divider-${index}`} />);
                    }

                    return items;
                })}
            </Menu>
        </>
    );
};

const Toolbar = (props: ResponsiveToolbarProps) => {
    const { disabled, multi, options, selected, onSelect } = props;
    return (
        <StyledToggleButtonGroup
            exclusive={!multi}
            size={'small'}
            onChange={(e, val) => {
                if (typeof onSelect === 'function') {
                    onSelect(val);
                }
            }}
        >
            {Object.keys(options).map((key, index) => {
                const action: ActionType = options[key];
                return (
                    <ToggleButton
                        key={index}
                        value={key}
                        disabled={disabled || action.disabled}
                        selected={selected && Array.isArray(selected) ? selected.includes(key) : key === selected}
                        onClick={action.onClick}
                    >
                        <action.icon fontSize={'small'} />
                    </ToggleButton>
                );
            })}
        </StyledToggleButtonGroup>
    );
};

export default (props: ResponsiveToolbarProps) => {
    return props.isSmall ? <Dropdown {...props} /> : <Toolbar {...props} />;
};
